import type { AnalyticsActivityResponse } from '@carboncopyco/data';
import type { TimePeriod } from '@carboncopyco/types';
import { ColorHex } from '@carboncopyco/ui';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import HC_rounded from 'highcharts-rounded-corners';
import { useEffect, useState } from 'react';
import type { ColumnChartConfigType } from '../../types';
import { mapChartData } from '../../utils';
import { getBaseConfig } from './getBaseConfig';

HC_rounded(Highcharts);

interface ColumnChartProps {
  timePeriodValue: TimePeriod;
  visitedData: AnalyticsActivityResponse;
  presentedData: AnalyticsActivityResponse;
}
export const DoubleBarChart = ({
  timePeriodValue,
  visitedData,
  presentedData,
}: ColumnChartProps) => {
  const [columnChartData, setColumnChartData] = useState<ColumnChartConfigType>(
    {}
  );

  useEffect(() => {
    if (presentedData && visitedData) {
      getConfigData();
    }
  }, [presentedData, visitedData]);

  const getConfigData = () => {
    const columnStackChartDataCopy: ColumnChartConfigType = {};
    const xCategories: Array<string> = [];
    const resolution = presentedData?.metadata.resolution;
    const data =
      presentedData?.results?.length > 0
        ? presentedData.results
        : visitedData?.results;

    const visitedOccurrences = mapChartData(visitedData.results, 'occurrences');
    const presentedOccurrences = mapChartData(
      presentedData.results,
      'occurrences'
    );

    if (resolution === 'day') {
      columnStackChartDataCopy.step =
        timePeriodValue === '90' ? 7 : timePeriodValue === '60' ? 2 : 1;
    }

    const barRadius = resolution === 'day' ? '12%' : '5%';

    columnStackChartDataCopy.series = [
      {
        data: visitedOccurrences,
        color: ColorHex.accent1200,
        name: 'Sandbox visits',
        type: 'column',
        borderRadiusTopLeft: barRadius,
        borderRadiusTopRight: barRadius,
      },
      {
        data: presentedOccurrences,
        color: ColorHex.primary200,
        name: 'Demos given',
        type: 'column',
        borderRadiusTopLeft: barRadius,
        borderRadiusTopRight: barRadius,
      },
    ];

    const getMaxAmount = () => {
      const combinedOccurrences = presentedOccurrences.map(
        (value, index) => value + visitedOccurrences[index]
      );
      return Math.max(...combinedOccurrences);
    };

    if (getMaxAmount() <= 30) {
      columnStackChartDataCopy.tickAmount = 3;
    } else {
      columnStackChartDataCopy.tickAmount = 5;
    }

    data?.forEach((item) => {
      xCategories.push(item?.timestamp);
    });

    columnStackChartDataCopy.xCategories = xCategories;
    columnStackChartDataCopy.zoomEnabled = false;
    columnStackChartDataCopy.chartHeight = 200;
    columnStackChartDataCopy.resolution = resolution;
    columnStackChartDataCopy.isLegendEnabled = true;

    setColumnChartData(columnStackChartDataCopy);
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <HighchartsReact
          highcharts={Highcharts}
          options={getBaseConfig(columnChartData)}
        />
      </div>
    </div>
  );
};
